import React, { useState } from 'react';
import $ from 'legacy/jquery';

import TrayContainer from '@grnhse/seedling/lib/azalea/components/trays/tray_container';
import Tray from '@grnhse/seedling/lib/azalea/components/trays/tray';
import { fetchBulkActions } from './client';
import styled from 'styled-components';
import { useEffectOnce, useInterval, useLocalStorage } from 'react-use';
import t from 'shared/utils/translation';
import { CSSTransitionGroup } from 'react-transition-group';
import BulkActionList from './bulk_action_list';
import {
  isUnresolved,
  isResolved,
  allAcknowledged,
  anyUnresolved,
  isDismissed,
} from './bulk_action';
import { trackEvent } from '@grnhse/datacoral_tracker';
import type { BulkAction } from './bulk_action';

import { standardRequestWithFlash } from 'shared/utils/request';
import Modal from '@grnhse/seedling/lib/azalea/components/modals/modal_with_submit_actions';
// @ts-expect-error - TS7016 - Could not find a declaration file for module '@grnhse/seedling/lib/azalea/components/modals/use_modal'. 'node_modules/@grnhse/seedling/lib/azalea/components/modals/use_modal.js' implicitly has an 'any' type.
import useModal from '@grnhse/seedling/lib/azalea/components/modals/use_modal';
import { fontWeight } from '@grnhse/seedling/lib/azalea/constants';

const POLL_INTERVAL = 5000;

type Props = {
  path: string;
  cancelPath: string;
  initialBulkActions: Array<BulkAction>;
};

const BulkActionsTray = ({ path, cancelPath, initialBulkActions }: Props) => {
  const [bulkActions, setBulkActions] = useState(initialBulkActions);
  const [open, openModal, closeModal] = useModal(false);

  const [expanded, setExpanded] = useLocalStorage<boolean>(
    'bulk_actions_tray/expanded',
    true
  );

  const [dismissed, setDismissed] = useLocalStorage<Array<number>>(
    'bulk_actions_tray/dismissed',
    []
  );

  const [acknowledged, setAcknowledged] = useLocalStorage<Array<number>>(
    'bulk_actions_tray/acknowledged',
    []
  );

  useInterval(pollForStatus, anyUnresolved(bulkActions) ? POLL_INTERVAL : null);

  useEffectOnce(() => {
    $(document).on('greenhouse.bulk_actions.enqueued', async function () {
      await pollForStatus();

      if (visibleBulkActions().length === 0) {
        setExpandedAndTrack(true);
      }
    });
  });

  async function pollForStatus() {
    const newBulkActions = await fetchBulkActions(path);
    setBulkActions(newBulkActions);
  }

  function setExpandedAndTrack(newExpanded: boolean) {
    setExpanded(newExpanded);

    trackEvent('bulk-actions-tray:expand', {
      expanded: newExpanded,
      numActions: bulkActions.length,
    });
  }

  function acknowledgeAll() {
    setAcknowledged(bulkActions.map((bulkAction) => bulkAction.id));
  }

  function dismissResolved() {
    const actions = bulkActions.filter((action) => isResolved(action));

    setDismissed(actions.map((action) => action.id));
    trackEvent('bulk-actions-tray:dismiss', { numActions: actions.length });
  }

  function dismissAll() {
    setDismissed(bulkActions.map((action) => action.id));
    trackEvent('bulk-actions-tray:dismiss', { numActions: bulkActions.length });
  }

  function visibleBulkActions() {
    return bulkActions.filter(
      // @ts-expect-error - TS2345 - Argument of type 'never[] | undefined' is not assignable to parameter of type 'number[]'.
      (bulkAction) => !isDismissed(bulkAction, dismissed)
    );
  }

  function trayStatus() {
    const anyFailures = bulkActions.some(
      (bulkAction) =>
        bulkAction.status === 'failed' ||
        bulkAction.status === 'complete_with_errors'
    );

    const allComplete = bulkActions.every(
      (bulkAction) =>
        bulkAction.status === 'complete' || bulkAction.status === 'cancelled'
    );

    if (expanded) {
      return 'normal';
      // @ts-expect-error - TS2345 - Argument of type 'never[] | undefined' is not assignable to parameter of type 'number[]'.
    } else if (allAcknowledged(bulkActions, acknowledged)) {
      return 'normal';
    } else if (anyFailures) {
      return 'error';
    } else if (allComplete) {
      return 'success';
    } else if (anyUnresolved(bulkActions)) {
      return 'pending';
    } else {
      return 'normal';
    }
  }

  function trayTitle() {
    if (!expanded) {
      return t('bulk_actions.tray.title.minimized');
    } else if (anyUnresolved(bulkActions)) {
      return t('bulk_actions.tray.title.executing');
    } else {
      return t('bulk_actions.tray.title.idle');
    }
  }

  async function cancelAction(actionId: number) {
    await standardRequestWithFlash.post(cancelPath, { ids: [actionId] });
    pollForStatus();
  }

  async function cancelAll() {
    const allUnresolvedIds = bulkActions
      .filter((action) => isUnresolved(action))
      .map((action) => action.id);

    await standardRequestWithFlash.post(cancelPath, { ids: allUnresolvedIds });
    await pollForStatus();

    dismissAll();
    closeModal();

    trackEvent('bulk-actions-tray:cancel-all', {
      numActions: allUnresolvedIds.length,
    });
  }

  function handleOnClose(event: React.MouseEvent) {
    if (anyUnresolved(bulkActions)) {
      event.stopPropagation();
      openModal();
    } else {
      dismissResolved();
    }
  }

  return (
    <>
      <CSSTransitionGroup
        transitionName="tray-container"
        transitionLeaveTimeout={200}
        transitionEnterTimeout={200}
      >
        {visibleBulkActions().length > 0 && (
          <Outer
            key="outer"
            data-provides="bulk-action-tray"
            data-provides-of={expanded ? 'expanded' : 'minimized'}
          >
            <TrayContainer right={280} zIndex={998} position="fixed">
              <Tray
                title={trayTitle()}
                width={expanded ? 350 : 180}
                status={trayStatus()}
                expanded={!!expanded}
                onExpandChange={() => {
                  acknowledgeAll();
                  setExpandedAndTrack(!expanded);
                }}
                showCloseIcon={true}
                onClose={handleOnClose}
              >
                <BulkActionList
                  bulkActions={visibleBulkActions()}
                  cancelAction={cancelAction}
                />
              </Tray>
            </TrayContainer>
          </Outer>
        )}
      </CSSTransitionGroup>

      <Modal
        open={open}
        closeModal={closeModal}
        onSubmit={cancelAll}
        title={t('bulk_actions.modal.title')}
        submitText={t('bulk_actions.modal.submit')}
        cancelText={t('bulk_actions.modal.cancel')}
        fancy={true}
        dividers={false}
        draggable={false}
        maxWidth="sm"
      >
        <ConfirmText>{t('bulk_actions.modal.body')}</ConfirmText>
      </Modal>
    </>
  );
};

const ConfirmText = styled.p`
  ${fontWeight('normal')}
`;

const Outer = styled.div`
  font-family: 'Untitled Sans', sans-serif;
  transition: max-height 200ms;

  &.tray-container-height-leave,
  &.tray-container-height-enter.tray-container-height-enter-active {
    max-height: 150px;
  }

  &.tray-container-height-leave.tray-container-height-leave-active,
  &.tray-container-height-enter {
    max-height: 0;
  }
`;

export default BulkActionsTray;
